import { useEffect, useState } from "react";
import { Search, Loader2, Scissors, ArrowLeft, ListVideo, Users, Sparkles, Play, Film, Trophy, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import Login from "./Login";
import {
  listShows, archiveSearch, makeClipFromRange, detectGuests, makeGuestClip, genChapters, getChapters,
  makeReel, guestLeaderboard,
} from "../lib/api";

const ts = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;
const MODE_Q = { oneliner: "one liners", story: "personal stories", testimonial: "testimonials", barths_take: "Barth's perspective" };
const MODES = [
  { key: "barths_take", label: "Barth's take", hint: "Where Barth shares his own opinion or perspective (first-person)." },
  { key: "oneliner", label: "One-liners", hint: "Your quick-witted comebacks — setup included." },
  { key: "story", label: "Barth's stories", hint: "Personal stories that relate to the caller." },
  { key: "testimonial", label: "Testimonials", hint: "What the show is doing for people." },
];

export default function Discover() {
  const { user, loading } = useAuth();
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [matches, setMatches] = useState(null);
  const [shows, setShows] = useState([]);
  const [showId, setShowId] = useState("");
  const [chapters, setChapters] = useState([]);
  const [guests, setGuests] = useState([]);
  const [busy, setBusy] = useState("");
  const [mode, setMode] = useState(""); // "" | oneliner | story | testimonial
  const [board, setBoard] = useState(null);

  useEffect(() => { if (user) listShows().then((s) => setShows(s.filter((x) => x.status === "completed"))); }, [user]);

  if (loading) return <div className="grid h-screen place-items-center bg-background text-accent"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!user) return <Login />;

  const runSearch = async () => {
    const query = q.trim() || MODE_Q[mode] || "";
    if (!query) return;
    setSearching(true); setMatches(null);
    try { const d = await archiveSearch(query, undefined, mode || undefined); setMatches(d.matches || []); }
    catch { toast.error("Search failed"); } finally { setSearching(false); }
  };

  const makeReelNow = async () => {
    const query = q.trim() || MODE_Q[mode] || "";
    if (!query) { toast.error("Type what the reel is about first"); return; }
    setBusy("reel");
    try {
      const d = await makeReel({ query, preset: mode || undefined, max_min: 5 });
      toast.success(`Reel building from ${d.segments} moments — find it in your clips`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build reel"); }
    finally { setBusy(""); }
  };

  const loadBoard = async () => {
    setBusy("board");
    try { const d = await guestLeaderboard(); setBoard(d.guests || []); }
    catch { toast.error("Could not load leaderboard"); } finally { setBusy(""); }
  };

  const clipIt = async (m) => {
    setBusy(`clip-${m.show_id}-${m.start}`);
    try {
      await makeClipFromRange(m.show_id, { start: m.start, end: m.end, title: m.title });
      toast.success("Clip is generating — find it in that show");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create clip"); }
    finally { setBusy(""); }
  };

  const loadShow = async (id) => {
    setShowId(id); setChapters([]); setGuests([]);
    if (!id) return;
    try { const c = await getChapters(id); setChapters(c.chapters || []); } catch {}
  };
  const doChapters = async () => {
    setBusy("chapters");
    try { const d = await genChapters(showId); setChapters(d.chapters || []); toast.success(`${d.chapters.length} chapters`); }
    catch { toast.error("Could not build chapters"); } finally { setBusy(""); }
  };
  const doGuests = async () => {
    setBusy("guests");
    try { const d = await detectGuests(showId); setGuests(d.guests || []); toast.success(`${d.guests.length} guests found`); }
    catch { toast.error("Could not detect guests"); } finally { setBusy(""); }
  };
  const guestClip = async (name, mode) => {
    setBusy(`g-${name}-${mode}`);
    try { await makeGuestClip(showId, { name, mode, target_min: 12 }); toast.success(`${mode === "full" ? "Full interview" : "Best-of"} generating for ${name}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-background text-white" data-testid="discover-page">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <a href="/" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-accent"><ArrowLeft className="h-4 w-4" /> Dashboard</a>
          <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-accent"><Search className="h-4 w-4" /> Discover</span>
        </div>
      </header>

      <div className="mx-auto max-w-4xl space-y-10 px-6 py-10">
        {/* Archive semantic search */}
        <section>
          <h1 className="font-display text-4xl tracking-wide">Ask your archive anything</h1>
          <p className="mt-2 font-body text-sm text-muted-foreground">Search every moment across all your shows — "every time we talked about faith" — and clip it instantly.</p>
          <div className="mt-5 flex gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input data-testid="archive-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && runSearch()}
                     placeholder="e.g. every time we talked about how Jesus can help you"
                     className="w-full rounded-none border border-border bg-card py-3 pl-10 pr-3 font-mono text-sm outline-none focus:border-accent" />
            </div>
            <button data-testid="archive-search-btn" onClick={runSearch} disabled={searching}
                    className="bg-accent px-6 font-body text-sm font-semibold text-black disabled:opacity-50">
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
            </button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {MODES.map((m) => (
              <button key={m.key} data-testid={`mode-${m.key}`} onClick={() => setMode((v) => (v === m.key ? "" : m.key))}
                      className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-xs uppercase tracking-wider transition-colors ${mode === m.key ? "bg-accent text-black" : "border border-border text-muted-foreground hover:border-accent hover:text-accent"}`}>
                <Zap className="h-3.5 w-3.5" /> {m.label}
              </button>
            ))}
            <button data-testid="make-reel-btn" onClick={makeReelNow} disabled={busy === "reel"}
                    className="flex items-center gap-1.5 border border-accent px-3 py-1.5 font-body text-xs font-semibold text-accent transition-colors hover:bg-accent hover:text-black disabled:opacity-50">
              {busy === "reel" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Film className="h-3.5 w-3.5" />} Make reel from this search
            </button>
            {mode && <span className="w-full font-body text-xs text-muted-foreground">{MODES.find((m) => m.key === mode)?.hint}</span>}
          </div>

          {matches && (
            <div className="mt-6 space-y-3" data-testid="search-results">
              {matches.length === 0 && <p className="font-body text-sm text-muted-foreground">No matches found — try different words.</p>}
              {matches.map((m, i) => (
                <div key={i} className="flex items-start justify-between gap-4 border border-border bg-card p-4" data-testid={`match-${i}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-mono text-[11px] text-muted-foreground">
                      <span className="bg-accent px-1.5 py-0.5 font-bold text-black">{m.relevance}</span>
                      <span className="uppercase tracking-widest">{m.show_title}</span>
                      <span>· {ts(m.start)}–{ts(m.end)}</span>
                    </div>
                    <p className="mt-1.5 font-body text-sm font-semibold">{m.title}</p>
                    {m.quote && <p className="mt-1 line-clamp-2 font-body text-xs italic text-muted-foreground">"{m.quote}"</p>}
                  </div>
                  <button data-testid={`clip-btn-${i}`} onClick={() => clipIt(m)} disabled={busy === `clip-${m.show_id}-${m.start}`}
                          className="flex shrink-0 items-center gap-1.5 border border-accent px-3 py-2 font-body text-xs font-semibold text-accent transition-colors hover:bg-accent hover:text-black disabled:opacity-50">
                    {busy === `clip-${m.show_id}-${m.start}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Scissors className="h-3.5 w-3.5" />} Clip
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Episode tools: chapters + guests */}
        <section className="border-t border-border pt-8">
          <h2 className="font-display text-3xl tracking-wide">Episode tools</h2>
          <p className="mt-2 font-body text-sm text-muted-foreground">Pick a show to build a browsable chapter list or pull a guest's full/best-of interview.</p>
          <select data-testid="episode-select" value={showId} onChange={(e) => loadShow(e.target.value)}
                  className="mt-4 w-full max-w-md rounded-none border border-border bg-card px-3 py-2.5 font-mono text-sm outline-none focus:border-accent">
            <option value="">Select a show…</option>
            {shows.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>

          {showId && (
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              {/* Chapters */}
              <div className="border border-border bg-card p-5">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 font-body text-sm font-semibold"><ListVideo className="h-4 w-4 text-accent" /> Chapters</span>
                  <button data-testid="gen-chapters-btn" onClick={doChapters} disabled={busy === "chapters"}
                          className="flex items-center gap-1.5 border border-border px-3 py-1.5 font-body text-xs hover:border-accent hover:text-accent disabled:opacity-50">
                    {busy === "chapters" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Build
                  </button>
                </div>
                <div className="mt-4 space-y-2" data-testid="chapters-list">
                  {chapters.length === 0 ? <p className="font-mono text-xs text-muted-foreground">No chapters yet — click Build.</p> :
                    chapters.map((c, i) => (
                      <div key={i} className="border-l-2 border-accent/40 pl-3">
                        <p className="font-mono text-xs text-accent">{ts(c.start)}</p>
                        <p className="font-body text-sm font-semibold">{c.title}</p>
                        {c.caption && <p className="font-body text-xs text-muted-foreground">{c.caption}</p>}
                      </div>
                    ))}
                </div>
              </div>

              {/* Guests */}
              <div className="border border-border bg-card p-5">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 font-body text-sm font-semibold"><Users className="h-4 w-4 text-accent" /> Guests</span>
                  <button data-testid="detect-guests-btn" onClick={doGuests} disabled={busy === "guests"}
                          className="flex items-center gap-1.5 border border-border px-3 py-1.5 font-body text-xs hover:border-accent hover:text-accent disabled:opacity-50">
                    {busy === "guests" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Detect
                  </button>
                </div>
                <div className="mt-4 space-y-3" data-testid="guests-list">
                  {guests.length === 0 ? <p className="font-mono text-xs text-muted-foreground">No guests yet — click Detect.</p> :
                    guests.map((g, i) => (
                      <div key={i} className="border border-border p-3">
                        <p className="font-body text-sm font-semibold">{g.name}</p>
                        <p className="font-mono text-[10px] text-muted-foreground">{Math.round(g.total_sec / 60)} min · {g.ranges.length} segments</p>
                        <div className="mt-2 flex gap-2">
                          <button data-testid={`guest-full-${i}`} onClick={() => guestClip(g.name, "full")} disabled={busy === `g-${g.name}-full`}
                                  className="flex items-center gap-1 border border-accent px-2.5 py-1 font-body text-[11px] font-semibold text-accent hover:bg-accent hover:text-black disabled:opacity-50">
                            {busy === `g-${g.name}-full` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Full interview
                          </button>
                          <button data-testid={`guest-best-${i}`} onClick={() => guestClip(g.name, "best")} disabled={busy === `g-${g.name}-best`}
                                  className="flex items-center gap-1 border border-border px-2.5 py-1 font-body text-[11px] hover:border-accent hover:text-accent disabled:opacity-50">
                            {busy === `g-${g.name}-best` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Scissors className="h-3 w-3" />} Best of
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Guest leaderboard */}
        <section className="border-t border-border pt-8">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-display text-3xl tracking-wide"><Trophy className="h-6 w-6 text-accent" /> Guest leaderboard</h2>
            <button data-testid="load-leaderboard-btn" onClick={loadBoard} disabled={busy === "board"}
                    className="flex items-center gap-1.5 border border-border px-3 py-1.5 font-body text-xs hover:border-accent hover:text-accent disabled:opacity-50">
              {busy === "board" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trophy className="h-3.5 w-3.5" />} Rank guests
            </button>
          </div>
          <p className="mt-2 font-body text-sm text-muted-foreground">Which guests pull the best clips (from your Keep/Kill/Love + shares) — so you know who's worth a full interview. Detect guests on your shows first.</p>
          {board && (
            <div className="mt-4 space-y-2" data-testid="leaderboard">
              {board.length === 0 ? <p className="font-mono text-xs text-muted-foreground">No ranked guests yet — detect guests on a few shows, then rank.</p> :
                board.map((g, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 border border-border bg-card p-4" data-testid={`board-row-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className="font-display text-2xl text-accent">{i + 1}</span>
                      <div>
                        <p className="font-body text-sm font-semibold">{g.name}
                          {g.worth_interview && <span className="ml-2 bg-accent px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase text-black">worth a full interview</span>}
                        </p>
                        <p className="font-mono text-[11px] text-muted-foreground">{g.talk_min} min · {g.clips} clips · ❤ {g.loves} · keep {g.keeps} · kill {g.kills} · shares {g.shares}</p>
                      </div>
                    </div>
                    <span className="font-display text-xl text-white">{g.score}</span>
                  </div>
                ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
